import type { ComponentProps, ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { ErrorFallback } from '#core/components/error/ErrorFallback.tsx';

import { ToolContentArea } from './ToolContentArea.tsx';
import classes from './ToolDisplay.module.css';
import { Bubble } from '#plugin-chat/components/bubble/Bubble.tsx';
import { Shimmer } from '#plugin-chat/components/shared/Shimmer.tsx';
import { usePartStatus } from '#plugin-chat/hooks/usePartStatus.ts';

export interface ToolContentBubbleProps {
  children: ReactNode;
  /** Enable shimmer effect during streaming (default: true). */
  shimmer?: boolean;
  kind?: ComponentProps<typeof Bubble>['kind'];
}

/**
 * The standard content surface: the grid content region wrapped in an
 * assistant bubble with an error boundary and a shimmer while the tool
 * is still streaming. The default composition for rich status renderers
 * (dashboards, insights, streamed prose).
 */
/**
 * Shared error-boundary fallback for the chrome: an error bubble in the
 * content region. Used by both `ToolContentBubble`'s inner boundary and
 * `ToolDisplay`'s outer boundary around status renderers.
 */
export function ToolContentErrorFallback(props: ComponentProps<typeof ErrorFallback>) {
  const { t } = useTranslation('chat');
  return (
    <Bubble kind="error" role="assistant">
      <ErrorFallback {...props} showDetails title={t('somethingWentWrong')} variant="alert" />
    </Bubble>
  );
}

export function ToolContentBubble({ children, shimmer = true, kind }: ToolContentBubbleProps) {
  const { isRunning } = usePartStatus();

  return (
    <ToolContentArea>
      <div className={classes.contentBox}>
        <ErrorBoundary FallbackComponent={ToolContentErrorFallback}>
          <Bubble kind={kind} role="assistant">
            <Shimmer
              color="color-mix(in oklab, var(--mantine-color-dark-3), transparent 80%)"
              duration={1000}
              enabled={shimmer && isRunning}
              repeatDelay={500}
              style={{ width: '100%' }}
              variant="background"
            >
              {children}
            </Shimmer>
          </Bubble>
        </ErrorBoundary>
      </div>
    </ToolContentArea>
  );
}
